import { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useToast } from '../components/ui/Toast';
import type { PermitRecord } from '../types';
import { PERMIT_PORTALS } from '../config/permitPortals';

const JURISDICTIONS = ['Ontario', 'Rancho Cucamonga', 'Fontana', 'Riverside', 'San Bernardino', 'Palm Desert', 'Indio'];
const PERMIT_TYPES = ['building', 'electrical', 'plumbing', 'mechanical', 'demolition', 'grading', 'tenant_improvement', 'new_construction'];
const STATUSES = ['issued', 'in_review', 'approved', 'expired', 'final'];

function formatCurrency(val?: number | null): string {
  if (!val) return '—';
  return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const TYPE_COLORS: Record<string, string> = {
  building: 'bg-info-wash text-info',
  electrical: 'bg-warning-wash text-warning',
  plumbing: 'bg-info-wash text-info',
  mechanical: 'bg-warning-wash text-warning',
  demolition: 'bg-red-100 text-red-800',
  grading: 'bg-warning-wash text-warning',
  tenant_improvement: 'bg-gray-100 text-gray-800',
  new_construction: 'bg-success-wash text-success',
};

const STATUS_COLORS: Record<string, string> = {
  issued: 'bg-info-wash text-info',
  in_review: 'bg-warning-wash text-warning',
  approved: 'bg-success-wash text-success',
  expired: 'bg-red-100 text-red-800',
  final: 'bg-gray-100 text-gray-800',
};

export default function PermitMonitor() {
  const { toast } = useToast();
  const [tab, setTab] = useState<'browse' | 'scan' | 'stats'>('browse');

  // Browse state
  const [permits, setPermits] = useState<PermitRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    jurisdiction: '', permit_type: '', status: '', is_industrial: false,
    search: '', date_from: '', date_to: '', min_valuation: '',
  });

  // Scan state
  const [scanMode, setScanMode] = useState<'pdf' | 'url' | 'portal' | 'search'>('pdf');
  const [scanJurisdiction, setScanJurisdiction] = useState('Ontario');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [previewPermit, setPreviewPermit] = useState<Record<string, any> | null>(null);
  const [portalPermits, setPortalPermits] = useState<Record<string, any>[]>([]);
  const [scanUrl, setScanUrl] = useState('');
  const [showPortals, setShowPortals] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Live PermitStack search state
  const [searchCity, setSearchCity] = useState('');
  const [searchAddress, setSearchAddress] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [savedSearchIds, setSavedSearchIds] = useState<Set<string>>(new Set());

  // "On my properties" state (nightly feed matches)
  const [onPropsPermits, setOnPropsPermits] = useState<any[]>([]);
  const [lastFeedRun, setLastFeedRun] = useState<any | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Manual entry state
  const [showManual, setShowManual] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({
    jurisdiction: 'Ontario', permit_number: '', permit_type: 'building', status: 'issued',
    description: '', property_address: '', city: '', zip: '', apn: '',
    applicant_name: '', contractor_name: '', valuation: '', issue_date: '', final_date: '',
    is_industrial: false,
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Stats state
  const [stats, setStats] = useState<Record<string, any>>({});

  const loadPermits = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { limit: '50', offset: String(page * 50) };
      if (filters.jurisdiction) params.jurisdiction = filters.jurisdiction;
      if (filters.permit_type) params.permit_type = filters.permit_type;
      if (filters.status) params.status = filters.status;
      if (filters.is_industrial) params.is_industrial = 'true';
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.min_valuation) params.min_valuation = filters.min_valuation;
      const res = await api.listPermits(params);
      setPermits(res.permits);
      setTotal(res.total);
    } catch (err) {
      console.error('Failed to load permits:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { if (tab === 'browse') loadPermits(); }, [tab, loadPermits]);

  useEffect(() => {
    if (tab === 'stats') {
      api.getPermitStats().then((r) => setStats(r.stats || {})).catch(console.error);
    }
  }, [tab]);

  // ── Scan handlers ──

  const handleScanPDF = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) { setScanMsg('Select a PDF file first'); return; }
    setScanLoading(true); setScanMsg(''); setPreviewPermit(null);
    try {
      const res = await api.scanPermitPDF(file, scanJurisdiction, false);
      setPreviewPermit(res.extracted);
      setScanMsg('Preview ready — review and save below');
    } catch (err: any) {
      setScanMsg(`Error: ${err.message}`);
    } finally {
      setScanLoading(false);
    }
  };

  const handleScanURL = async () => {
    if (!scanUrl.trim()) { setScanMsg('Enter a URL first'); return; }
    setScanLoading(true); setScanMsg(''); setPreviewPermit(null);
    try {
      const res = await api.scanPermitURL(scanUrl.trim(), scanJurisdiction, false);
      setPreviewPermit(res.extracted);
      setScanMsg('Preview ready — review and save below');
    } catch (err: any) {
      setScanMsg(`Error: ${err.message}`);
    } finally {
      setScanLoading(false);
    }
  };

  const handleScrapePortal = async () => {
    if (!scanUrl.trim()) { setScanMsg('Enter a portal URL first'); return; }
    setScanLoading(true); setScanMsg(''); setPortalPermits([]);
    try {
      const res = await api.scrapePermitPortal(scanUrl.trim(), scanJurisdiction, false);
      setPortalPermits(res.permits || []);
      setScanMsg(`Found ${res.count} permits — review below`);
    } catch (err: any) {
      setScanMsg(`Error: ${err.message}`);
    } finally {
      setScanLoading(false);
    }
  };

  const handleSearchPermits = async () => {
    if (!searchCity.trim() && !searchAddress.trim()) { setScanMsg('Enter a city or address'); return; }
    setSearchLoading(true); setScanMsg('');
    try {
      const res = await api.searchLivePermits({
        city: searchCity.trim() || undefined,
        address: searchAddress.trim() || undefined,
      });
      setSearchResults(res.permits || []);
      setScanMsg(res.permits?.length ? `Found ${res.permits.length} permits` : 'No permits found');
    } catch (err: any) {
      setScanMsg(`Error: ${err.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  const savePermitFromSearch = async (p: any) => {
    try {
      await api.createPermit({
        source_system: 'permitstack', source_record_id: p.id,
        jurisdiction: p.jurisdiction_name || p.address_city || 'unknown',
        permit_number: p.permit_number, status: p.status, description: p.description_raw,
        property_address: p.address_street, city: p.address_city, state: p.address_state || 'CA',
        zip: p.address_zip, applicant_name: p.owner_name, contractor_name: p.contractor_name,
        valuation: p.estimated_value != null && p.estimated_value !== '' ? Number(p.estimated_value) : null,
        issue_date: p.date_issued || null, applied_date: p.date_filed || null,
        final_date: p.date_completed || null, matched_property_id: p.matched_property_id || null,
        raw_data: p,
      });
      setSavedSearchIds((s) => new Set(s).add(p.id));
    } catch (err: any) {
      setScanMsg(`Error saving: ${err.message}`);
    }
  };

  const loadOnProps = useCallback(async () => {
    try {
      const [permitsRes, statusRes] = await Promise.all([
        api.listPermits({ matched: 'true', limit: '25' }),
        api.permitIngestStatus(),
      ]);
      setOnPropsPermits(permitsRes.permits || []);
      const runs = statusRes.runs || [];
      setLastFeedRun(runs.find((r: any) => r.feed_code === 'permitstack') || null);
    } catch (err) {
      console.error('Failed to load matched permits:', err);
    }
  }, []);

  useEffect(() => { if (tab === 'browse') loadOnProps(); }, [tab, loadOnProps]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      // Pull both feeds: PermitStack (Riverside Co + Coachella Valley) and the
      // ArcGIS dev feed (SB corridor: Fontana + Rancho Cucamonga).
      const [ps, ag] = await Promise.allSettled([api.permitIngestNow(), api.permitIngestArcgis()]);
      const sum = (r: PromiseSettledResult<any>) =>
        r.status === 'fulfilled' ? (r.value.run || {}) : null;
      const p = sum(ps), a = sum(ag);
      const parts: string[] = [];
      if (p) parts.push(`PermitStack: ${p.promoted ?? 0} saved`);
      else parts.push('PermitStack: failed');
      if (a) parts.push(`Fontana/RC: ${a.promoted ?? 0} saved`);
      else parts.push('Fontana/RC: failed');
      // After both feeds land, raise alerts for permits matched to your properties.
      try {
        const al = await api.permitRunAlerts();
        if ((al.alerted ?? 0) > 0) parts.push(`${al.alerted} new alert${al.alerted === 1 ? '' : 's'}`);
      } catch { /* non-fatal */ }
      toast.success('Permit sync complete', parts.join(', '));
      await Promise.all([loadOnProps(), loadPermits()]);
    } catch (err: any) {
      toast.error('Permit sync failed', err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSavePreview = async () => {
    if (!previewPermit) return;
    setSaving(true); setScanMsg('');
    try {
      await api.createPermit({ ...previewPermit });
      setScanMsg('Permit saved successfully');
      setPreviewPermit(null);
    } catch (err: any) {
      setScanMsg(`Error saving: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleImportPortalPermits = async () => {
    if (portalPermits.length === 0) return;
    setSaving(true); setScanMsg('');
    try {
      const rows = portalPermits.map((p) => ({
        ...p,
        valuation: p.valuation ? String(p.valuation) : '',
      }));
      const res = await api.importPermits({ rows, jurisdiction: scanJurisdiction });
      setScanMsg(res.message || `Imported ${portalPermits.length} permits`);
      setPortalPermits([]);
    } catch (err: any) {
      setScanMsg(`Error importing: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleManualSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.jurisdiction) return;
    setSaving(true); setSaveMsg('');
    try {
      const data = { ...form };
      if (data.valuation) data.valuation = parseFloat(data.valuation);
      else delete data.valuation;
      await api.createPermit(data);
      setSaveMsg('Permit created successfully');
      setForm({
        jurisdiction: form.jurisdiction, permit_number: '', permit_type: 'building', status: 'issued',
        description: '', property_address: '', city: '', zip: '', apn: '',
        applicant_name: '', contractor_name: '', valuation: '', issue_date: '', final_date: '',
        is_industrial: false,
      });
    } catch (err: any) {
      setSaveMsg(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'browse' as const, label: 'Browse' },
    { key: 'scan' as const, label: 'Scan & Import' },
    { key: 'stats' as const, label: 'Stats' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Permit Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">Track building permits across IE and Coachella Valley jurisdictions</p>
        </div>

        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key ? 'border-primary-500 text-primary-500' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Browse Tab */}
        {tab === 'browse' && (
          <div className="space-y-4">
            {/* On my properties: nightly-feed permits matched to master properties */}
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    On my properties
                    {onPropsPermits.length > 0 && (
                      <span className="ml-2 text-xs bg-primary-100 text-primary-500 rounded-full px-2 py-0.5">{onPropsPermits.length}</span>
                    )}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Permits auto-matched to your properties by the nightly sync
                    {lastFeedRun ? ` — last run ${new Date(lastFeedRun.started_at).toLocaleString()} (${lastFeedRun.status})` : ' — no runs yet'}
                  </p>
                </div>
                <button
                  onClick={handleSyncNow}
                  disabled={syncing}
                  className="px-3 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                >
                  {syncing ? 'Syncing...' : 'Sync now'}
                </button>
              </div>
              {onPropsPermits.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="py-2 pr-3">Permit #</th>
                        <th className="py-2 pr-3">Type</th>
                        <th className="py-2 pr-3">Property</th>
                        <th className="py-2 pr-3">City</th>
                        <th className="py-2 pr-3">Filed / Issued</th>
                        <th className="py-2 pr-3">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {onPropsPermits.map((p: any) => (
                        <tr key={p.id} className="border-b border-gray-50">
                          <td className="py-2 pr-3 font-medium text-gray-900">{p.permit_number || '—'}</td>
                          <td className="py-2 pr-3">{p.permit_type || '—'}</td>
                          <td className="py-2 pr-3">
                            <a href={`/data-hub/${p.matched_property_id}`} className="text-primary-500 hover:underline">
                              {p.matched_property?.address || p.property_address || 'View property'}
                            </a>
                          </td>
                          <td className="py-2 pr-3">{p.city || '—'}</td>
                          <td className="py-2 pr-3">{p.applied_date || p.issue_date || '—'}</td>
                          <td className="py-2 pr-3">{formatCurrency(p.valuation)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-400">No matched permits yet — run a sync or wait for tonight's pull.</p>
              )}
            </div>

            <div className="bg-white rounded-xl border p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                <select value={filters.jurisdiction} onChange={(e) => { setFilters((f) => ({ ...f, jurisdiction: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">All Jurisdictions</option>
                  {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
                </select>
                <select value={filters.permit_type} onChange={(e) => { setFilters((f) => ({ ...f, permit_type: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">All Types</option>
                  {PERMIT_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select>
                <select value={filters.status} onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">All Statuses</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={filters.is_industrial} onChange={(e) => { setFilters((f) => ({ ...f, is_industrial: e.target.checked })); setPage(0); }} className="rounded" />
                  Industrial
                </label>
                <input type="text" placeholder="Search..." value={filters.search} onChange={(e) => { setFilters((f) => ({ ...f, search: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input type="date" value={filters.date_from} onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input type="date" value={filters.date_to} onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input type="number" placeholder="Min $" value={filters.min_valuation} onChange={(e) => { setFilters((f) => ({ ...f, min_valuation: e.target.value })); setPage(0); }} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>

            <div className="bg-white rounded-xl border overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-gray-400">Loading...</div>
              ) : permits.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No permits found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permit #</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jurisdiction</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valuation</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ind.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {permits.map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900 font-medium whitespace-nowrap">{p.permit_number || '—'}</td>
                          <td className="px-3 py-2 text-gray-700">{p.property_address || '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{p.jurisdiction}</td>
                          <td className="px-3 py-2">
                            {p.permit_type && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[p.permit_type] || 'bg-gray-100 text-gray-800'}`}>
                                {p.permit_type.replace(/_/g, ' ')}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {p.status && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-800'}`}>
                                {p.status.replace(/_/g, ' ')}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-500 text-xs max-w-[200px] truncate">{p.description || '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(p.valuation)}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{p.issue_date || '—'}</td>
                          <td className="px-3 py-2 text-center">
                            {p.is_industrial && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-wash text-success">IND</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {total > 50 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Showing {page * 50 + 1}–{Math.min((page + 1) * 50, total)} of {total}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50">Previous</button>
                  <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * 50 >= total} className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Scan & Import Tab */}
        {tab === 'scan' && (
          <div className="space-y-6">
            {/* Jurisdiction portal directory (URLs live-verified 2026-07-06) */}
            <div className="bg-white rounded-xl border p-4">
              <button
                onClick={() => setShowPortals((s) => !s)}
                className="w-full flex items-center justify-between text-left"
              >
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Permit Portals</h3>
                  <p className="text-xs text-gray-500">Official portals for your jurisdictions - open one, or send it to the URL scanner.</p>
                </div>
                <span className="text-gray-400 text-sm">{showPortals ? '▾' : '▸'}</span>
              </button>
              {showPortals && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {PERMIT_PORTALS.map((p) => (
                    <div key={p.jurisdiction} className="flex items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">{p.jurisdiction}</span>
                          <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 rounded px-1.5 py-0.5 shrink-0">{p.platform}</span>
                        </div>
                        {p.notes && <p className="text-xs text-gray-400 truncate">{p.notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a
                          href={p.portalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-1 text-xs font-medium text-primary-500 bg-primary-50 rounded-md hover:bg-primary-100"
                        >
                          Open
                        </a>
                        <button
                          onClick={() => { setScanMode('url'); setScanUrl(p.portalUrl); setScanMsg(''); }}
                          className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded-md hover:bg-gray-100"
                          title="Pre-fill the URL scanner with this portal"
                        >
                          Scan
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mode selector + jurisdiction */}
            <div className="bg-white rounded-xl border p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  {([['search', 'Search Permits'], ['pdf', 'PDF Upload'], ['url', 'URL Scan'], ['portal', 'Portal Scrape']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => { setScanMode(key); setScanMsg(''); setPreviewPermit(null); setPortalPermits([]); }}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        scanMode === key ? 'bg-white text-primary-500 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mr-2">Jurisdiction:</label>
                  <select
                    value={scanJurisdiction}
                    onChange={(e) => setScanJurisdiction(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  >
                    {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* PDF Upload */}
            {scanMode === 'pdf' && (
              <div className="bg-white rounded-xl border p-6 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Upload Permit PDF</h3>
                <p className="text-xs text-gray-500">Upload a building permit PDF and AI will extract the permit details.</p>
                <div className="flex items-center gap-4">
                  <input ref={fileInputRef} type="file" accept=".pdf" className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-500 hover:file:bg-primary-100" />
                  <button
                    onClick={handleScanPDF}
                    disabled={scanLoading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                  >
                    {scanLoading ? 'Scanning...' : 'Scan PDF'}
                  </button>
                </div>
              </div>
            )}

            {/* URL Scan */}
            {scanMode === 'url' && (
              <div className="bg-white rounded-xl border p-6 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Scan Permit URL</h3>
                <p className="text-xs text-gray-500">Paste a permit detail page URL and AI will extract the data. Note: works on simple pages only - most portals (Accela, eTRAKiT) load data with JavaScript and cannot be scanned this way; use PDF upload for those.</p>
                <div className="flex items-center gap-4">
                  <input
                    type="url"
                    value={scanUrl}
                    onChange={(e) => setScanUrl(e.target.value)}
                    placeholder="https://permits.cityname.gov/permit/12345"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleScanURL}
                    disabled={scanLoading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                  >
                    {scanLoading ? 'Scanning...' : 'Scan URL'}
                  </button>
                </div>
              </div>
            )}

            {/* Portal Scrape */}
            {scanMode === 'portal' && (
              <div className="bg-white rounded-xl border p-6 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Scrape Permit Portal</h3>
                <p className="text-xs text-gray-500">Paste a permit portal listing page URL to extract multiple permits at once (up to 25).</p>
                <div className="flex items-center gap-4">
                  <input
                    type="url"
                    value={scanUrl}
                    onChange={(e) => setScanUrl(e.target.value)}
                    placeholder="https://permits.cityname.gov/search?status=issued"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleScrapePortal}
                    disabled={scanLoading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                  >
                    {scanLoading ? 'Scraping...' : 'Scrape Portal'}
                  </button>
                </div>
              </div>
            )}

            {/* Status message */}
            {scanMsg && (
              <div className={`text-sm px-4 py-2 rounded-lg ${scanMsg.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-success-wash text-success'}`}>
                {scanMsg}
              </div>
            )}

            {/* Live PermitStack search */}
            {scanMode === 'search' && (
              <div className="bg-white rounded-xl border p-6 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Search Permits (live)</h3>
                <p className="text-xs text-gray-500">
                  Search real permit filings by city or address. Strong coverage: Riverside County + Coachella Valley cities.
                  San Bernardino County cities (Ontario, Fontana, Rancho Cucamonga) are not yet covered by this data source.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={searchCity}
                    onChange={(e) => setSearchCity(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Any city</option>
                    {['Riverside', 'Moreno Valley', 'Perris', 'Beaumont', 'Banning', 'Calimesa', 'Corona', 'San Bernardino', 'Rialto', 'Colton', 'Yucaipa', 'Highland', 'Redlands', 'Ontario', 'Fontana', 'Rancho Cucamonga', 'Palm Desert', 'Indio', 'La Quinta', 'Palm Springs', 'Coachella'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={searchAddress}
                    onChange={(e) => setSearchAddress(e.target.value)}
                    placeholder="Address (optional)"
                    className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleSearchPermits}
                    disabled={searchLoading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                  >
                    {searchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>
                {searchResults.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                          <th className="py-2 pr-3">Permit #</th>
                          <th className="py-2 pr-3">Address</th>
                          <th className="py-2 pr-3">City</th>
                          <th className="py-2 pr-3">Status</th>
                          <th className="py-2 pr-3">Filed</th>
                          <th className="py-2 pr-3">Value</th>
                          <th className="py-2 pr-3">Match</th>
                          <th className="py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((p) => (
                          <tr key={p.id} className="border-b border-gray-50">
                            <td className="py-2 pr-3 font-medium text-gray-900">{p.permit_number || '—'}</td>
                            <td className="py-2 pr-3">{p.address_street || '—'}</td>
                            <td className="py-2 pr-3">{p.address_city || '—'}</td>
                            <td className="py-2 pr-3">{p.status || '—'}</td>
                            <td className="py-2 pr-3">{p.date_filed || p.date_issued || '—'}</td>
                            <td className="py-2 pr-3">{formatCurrency(p.estimated_value != null && p.estimated_value !== '' ? Number(p.estimated_value) : null)}</td>
                            <td className="py-2 pr-3">
                              {p.matched_property_id
                                ? <span className="text-xs bg-success-wash text-success rounded px-1.5 py-0.5">Linked</span>
                                : <span className="text-xs text-gray-400">—</span>}
                            </td>
                            <td className="py-2">
                              {savedSearchIds.has(p.id)
                                ? <span className="text-xs text-success font-medium">Saved</span>
                                : (
                                  <button
                                    onClick={() => savePermitFromSearch(p)}
                                    className="px-2 py-1 text-xs font-medium text-primary-500 bg-primary-50 rounded-md hover:bg-primary-100"
                                  >
                                    Save
                                  </button>
                                )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Single permit preview (PDF / URL modes) */}
            {previewPermit && (
              <div className="bg-white rounded-xl border p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Extracted Permit Preview</h3>
                  <button
                    onClick={handleSavePreview}
                    disabled={saving}
                    className="px-4 py-2 bg-success text-white rounded-lg text-sm font-medium hover:bg-success/85 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Permit'}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
                  {Object.entries(previewPermit).filter(([, v]) => v != null).map(([key, val]) => (
                    <div key={key}>
                      <p className="text-xs text-gray-500">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium text-gray-900">{typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Portal permits table */}
            {portalPermits.length > 0 && (
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Portal Results ({portalPermits.length} permits)</h3>
                  <button
                    onClick={handleImportPortalPermits}
                    disabled={saving}
                    className="px-4 py-2 bg-success text-white rounded-lg text-sm font-medium hover:bg-success/85 disabled:opacity-50"
                  >
                    {saving ? 'Importing...' : `Import All (${portalPermits.length})`}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Permit #</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Address</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Valuation</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Ind.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {portalPermits.map((p, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{p.permit_number || '—'}</td>
                          <td className="px-3 py-2">{p.property_address || '—'}</td>
                          <td className="px-3 py-2">
                            {p.permit_type && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[p.permit_type] || 'bg-gray-100 text-gray-800'}`}>
                                {p.permit_type.replace(/_/g, ' ')}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 max-w-[200px] truncate">{p.description || '—'}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(p.valuation)}</td>
                          <td className="px-3 py-2">{p.issue_date || '—'}</td>
                          <td className="px-3 py-2 text-center">{p.is_industrial ? 'Y' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Collapsible manual entry */}
            <div className="bg-white rounded-xl border">
              <button
                onClick={() => setShowManual(!showManual)}
                className="w-full flex items-center justify-between p-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <span>Manual Entry</span>
                <svg className={`w-4 h-4 transition-transform ${showManual ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showManual && (
                <div className="p-6 border-t">
                  <form onSubmit={handleManualSave} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Jurisdiction *</label>
                        <select value={form.jurisdiction} onChange={(e) => setForm((f: any) => ({ ...f, jurisdiction: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" required>
                          {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Permit Number</label>
                        <input type="text" value={form.permit_number} onChange={(e) => setForm((f: any) => ({ ...f, permit_number: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select value={form.permit_type} onChange={(e) => setForm((f: any) => ({ ...f, permit_type: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          {PERMIT_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select value={form.status} onChange={(e) => setForm((f: any) => ({ ...f, status: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <input type="text" value={form.property_address} onChange={(e) => setForm((f: any) => ({ ...f, property_address: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input type="text" value={form.city} onChange={(e) => setForm((f: any) => ({ ...f, city: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                        <input type="text" value={form.zip} onChange={(e) => setForm((f: any) => ({ ...f, zip: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">APN</label>
                        <input type="text" value={form.apn} onChange={(e) => setForm((f: any) => ({ ...f, apn: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Applicant</label>
                        <input type="text" value={form.applicant_name} onChange={(e) => setForm((f: any) => ({ ...f, applicant_name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contractor</label>
                        <input type="text" value={form.contractor_name} onChange={(e) => setForm((f: any) => ({ ...f, contractor_name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Valuation ($)</label>
                        <input type="number" value={form.valuation} onChange={(e) => setForm((f: any) => ({ ...f, valuation: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                        <input type="date" value={form.issue_date} onChange={(e) => setForm((f: any) => ({ ...f, issue_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Final Date</label>
                        <input type="date" value={form.final_date} onChange={(e) => setForm((f: any) => ({ ...f, final_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={form.is_industrial} onChange={(e) => setForm((f: any) => ({ ...f, is_industrial: e.target.checked }))} className="rounded" />
                          Industrial Property
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea value={form.description} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div className="flex items-center gap-4">
                      <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Permit'}
                      </button>
                      {saveMsg && (
                        <span className={`text-sm ${saveMsg.startsWith('Error') ? 'text-red-600' : 'text-success'}`}>{saveMsg}</span>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {tab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(stats).length === 0 ? (
              <div className="col-span-2 text-center text-gray-400 py-12">No permit data yet</div>
            ) : (
              Object.entries(stats).map(([jurisdiction, s]: [string, any]) => (
                <div key={jurisdiction} className="bg-white rounded-xl border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{jurisdiction}</h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Total</p>
                      <p className="text-2xl font-bold text-gray-900">{s.total}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Industrial</p>
                      <p className="text-2xl font-bold text-success">{s.industrial}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Total Valuation</p>
                      <p className="text-xl font-bold text-gray-900">{formatCurrency(s.total_valuation)}</p>
                    </div>
                  </div>
                  {s.by_type && Object.keys(s.by_type).length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-2">By Type</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(s.by_type).map(([type, count]: [string, any]) => (
                          <span key={type} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${TYPE_COLORS[type] || 'bg-gray-100 text-gray-800'}`}>
                            {type.replace(/_/g, ' ')}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
